import { StepperOrientation, StepperSelectionEvent } from '@angular/cdk/stepper';
import { ChangeDetectorRef, Component, inject, PLATFORM_ID, ViewChild, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { defer, firstValueFrom, map, Observable } from 'rxjs';
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { State, US_STATES } from '../../../../shared/general/models/us-states.model';
import { AddressRequest, Amenity, CreatePropertyImageRequest, CreatePropertyPricingRule, CreatePropertyRequest, PropertyAmenityBulkRequest, Timezone } from '../../models/property.model';
import { PropertyService } from '../../services/property.service';
import { AuthService } from '../../../../core/services/auth.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import confetti from 'canvas-confetti';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatError, MatFormField, MatInputModule, MatLabel } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SuccessError } from '../../../../shared/general/components/success-error/success-error';
import { PropertyPricingRuleService } from '../../services/property-pricing-rule.service';
import { GtmService } from '../../../../core/services/gtm.service';
import { CreatePropertyAddPropertyPricingForm } from './pricing-rule/create-property-add-property-pricing-form';
import { DialogService } from '../../../../shared/services/dialog.service';
import { LoaderComponent } from '../../../../shared/general/components/loader/loader.component';
import { SnackBarService } from '../../../../shared/services/snackbar.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-create-property',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatStepperModule,
    MatInputModule,
    MatFormField,
    MatLabel,
    MatError,
    MatSelectModule,
    MatDatepickerModule,
    MatCheckboxModule,
    SuccessError,
    DragDropModule,
    LoaderComponent,
    MatProgressBarModule,
    MatProgressSpinnerModule
],
  templateUrl: './create-property.html',
  styleUrl: './create-property.scss',
})
export class CreatePropertyComponent implements OnInit {
  private propertyService = inject(PropertyService);
  private pricingService = inject(PropertyPricingRuleService);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private authService = inject(AuthService);
  private router = inject(Router);
  private gtm = inject(GtmService);
  private platformId = inject(PLATFORM_ID);
  private snackbar = inject(SnackBarService);

  @ViewChild('stepper') stepper!: MatStepper;
  private dialogService = inject(DialogService);
  userId: string | undefined;
  propForm: FormGroup;
  stepperOrientation: Observable<StepperOrientation>;
  states: State[] = US_STATES;
  submitted = false;
  isProcessing = false;
  isSubmitting = false;
  successfulSubmission = false;
  isProcessingImages = false;
  processedImages = 0;
  totalImages = 0;
  profileForm!: FormGroup;
  timezones: Timezone[] = [];
  amenities: Amenity[] = [];
  amenitiesFormArray!: FormArray;
  categories: string[] = [ "Property Style", "Community Features", "Essential", "Comfort and Convenience", "Outdoor", 
    "Waterfront and Boating", "Luxury and Recreation", "Family and Pet-Friendly", 
    "Location-Specific", "Eco-Friendly and Smart", "Unique or Local", "Accessibility", "Safety and Practical"];
  images: File[] = [];
  imagePreviews: string[] = [];
  imgPreviews: { url: string | SafeResourceUrl; description: string; order: number }[] = [];
  maxImages = 15;
  propertyTypes = ['house', 'townhouse', 'apartment', 'condo', 'villa', 'manufactured'];
  isAmenitiesLoaded = false;
  propertyCreatedId: string | undefined;
  pricingRules: CreatePropertyPricingRule[] = [];
  propertyPublished = false;
  today: Date = new Date();
  oneYearLater: Date = new Date(this.today);
    isCheckingAddress = false;
  private getDraftKey() {
    return `property-draft-${this.userId}`;
  }

  private clearDraft() {
    localStorage.removeItem(this.getDraftKey());
  }

  submissionSteps: Array<{
    id: string;
    label: string;
    status: 'pending' | 'active' | 'completed' | 'error';
    detail?: string;
  }> = [];

  submissionError: string | null = null;
  partialPropertyId: string | null = null;

  private initializeSubmissionSteps(): void {
    this.submissionSteps = [
      { id: 'geocode',        label: 'Validating address location',          status: 'pending' },
      { id: 'create-address', label: 'Saving your address',                  status: 'pending' },
      { id: 'create-property',label: 'Creating your property listing',       status: 'pending' },
      { id: 'assign-user',    label: 'Linking listing to your account',      status: 'pending' },
      { id: 'pricing-rules',  label: 'Setting up pricing rules',             status: 'pending' },
      { id: 'images',         label: 'Uploading your photos',                status: 'pending' },
      { id: 'amenities',      label: 'Saving selected amenities',            status: 'pending' },
      { id: 'calendars',      label: 'Adding any custom date rules',         status: 'pending' },
    ];
    this.cdr.detectChanges();
  }

  private updateStep(id: string, status: 'active' | 'completed' | 'error', detail?: string): void {
    const step = this.submissionSteps.find(s => s.id === id);
    if (!step) return;
    step.status = status;
    if (detail) step.detail = detail;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  get submissionProgressPercent(): number {
    if (!this.submissionSteps.length) return 0;
    const done = this.submissionSteps.filter(s => s.status === 'completed').length;
    return Math.round((done / this.submissionSteps.length) * 100);
  }
  
  constructor() {
    const breakpointObserver = inject(BreakpointObserver);
    
    this.stepperOrientation = breakpointObserver
      .observe('(min-width: 800px)')
      .pipe(map(({matches}) => (matches ? 'horizontal' : 'vertical')));
    this.propForm = this.fb.group({
      // - Step 1
      address: this.fb.group({
        displayName: [''],
        street: ['', Validators.required],
        city: ['', Validators.required],
        state: ['', Validators.required],
        zipCode: ['', Validators.required],
        timezone: ['', Validators.required]
      }),
      // - Step 2
      info: this.fb.group({
        bedrooms: ['', Validators.required],
        sleeps: ['', Validators.required],
        bathrooms: ['', Validators.required],
        cost: ['', Validators.required],
        propertyType: ['', Validators.required],
        sqFt: ['', Validators.required],
        description: ['', [Validators.required, Validators.maxLength(10000)]],
      }),
      propertyCalendars: this.fb.array([]),
      amenities: this.fb.array([]),
      images: this.fb.array([])
    });
    (this.propForm.get('images') as FormArray).validator = this.minLengthArray(1);
  }

  async ngOnInit(): Promise<void> {
    if(isPlatformBrowser(this.platformId)) {
      document.body.scrollTop = 0;
      this.gtm.pageView({
        page_path: '/property/create',
        page_title: 'Horizinns | Create Property'
      });
    }
    
    this.propertyService.getAmenities().subscribe(amenities => {
      this.amenities = amenities.map(amenity => ({
        ...amenity, description: amenity.description ?? ''
      }));

      const newAmenitiesFormArray = this.fb.array(
        this.amenities.map(() => this.fb.control(false))
      );
      this.propForm.setControl('amenities', newAmenitiesFormArray);
      this.cdr.detectChanges();

      setTimeout(() => {
        this.isAmenitiesLoaded = true;
      },0);

      console.log('amenities', this.amenities, this.categories);
    });
  }

  ngAfterViewInit(): void {
    // If user has an existing draft, send them straight to payment (streamlined flow)
    this.authService.loggedInUserId$.subscribe(userId => {
      if (userId) {
        this.userId = userId;
        const savedDraftId = localStorage.getItem(this.getDraftKey());
        if (savedDraftId) {
          console.log('✅ Found existing draft — redirecting directly to payment:', savedDraftId);
          // Clear the draft key so we don't loop
          localStorage.removeItem(this.getDraftKey());
          this.router.navigate([`/payment/${savedDraftId}`]);
        }
      }
    });
  }

  private minLengthArray(min: number) {
    return (array: AbstractControl): Record<string, any> | null => {
      return (array as FormArray).length >= min ? null : { required: true };
    };
  }

  get addressGroup()  { return this.propForm.get('address') as FormGroup; }
  get infoGroup()     { return this.propForm.get('info') as FormGroup; }
  get seasonsGroup()  { return this.propForm.get('propertyCalendars') as FormArray; }
  get amenitiesGroup() { return this.propForm.get('amenities') as FormArray; }
  get imagesArray(): FormArray {
    return this.propForm.get('images') as FormArray;
  }
  get finalizeGroup() { return this.propForm.get('finalize') as FormGroup; }

  // updateSortOrder(index: number, event: Event) {
  //   const select = event.target as HTMLSelectElement;
  //   const newSortOrder = parseInt(select.value, 10);
  // }

  getSortOrderOptions(): number[] {
    return Array.from({ length: this.images.length }, (_, i) => i + 1);
  }

  onStepChange(event: StepperSelectionEvent) {
    console.log('Moved from step', event.previouslySelectedIndex, 'to step', event.selectedIndex);

    // === Pricing Rules Step (now at index 3 because of new Instructions step) ===
    if (event.selectedIndex === 3) {
      this.setDefaultPriceRule();
    }
  }

  /**
   * Validates address asynchronously and only moves to next step if valid + unique
   */
  validateAddressAndProceed(): void {
    const street = this.addressGroup.get('street')?.value?.trim();
    const city = this.addressGroup.get('city')?.value?.trim();
    const stateCode = (this.addressGroup.get('state')?.value as State)?.code;
    const zipCode = this.addressGroup.get('zipCode')?.value?.trim();

    // 1. Basic required field validation
    if (!street || !city || !stateCode || !zipCode) {
      this.addressGroup.markAllAsTouched();
      this.snackbar.open('Please fill in all required address fields.', 'OK', { duration: 4000 });
      return;
    }

    this.isCheckingAddress = true;

    const addressRequest: AddressRequest = {
      street,
      city,
      state: stateCode,
      zipCode,
      lat: 0,
      lng: 0,
      country: 'USA'
    };

    this.propertyService.addressExists(addressRequest).subscribe({
      next: (exists: boolean) => {
        this.isCheckingAddress = false;

        if (exists) {
          // === DUPLICATE FOUND ===
          this.showAddressExistsWarning();
          this.addressGroup.get('street')?.setErrors({ addressExists: true });
          //this.addressGroup.markAllAsTouched();
          // Do NOT call stepper.next() → stays on Address step
        } else {
          // === UNIQUE → proceed ===
          console.log('✅ Address is unique. Advancing to next step...');
          this.stepper.next();
        }
      },
      error: (err) => {
        this.isCheckingAddress = false;
        console.error('Error checking address:', err);
        this.snackbar.open('Unable to verify address. Please try again.', 'OK', { duration: 4000 });
      }
    });
  }

  private showAddressExistsWarning() {
    // Using Angular Material Snackbar (recommended)
    this.snackbar.open('This address already exists', ' ', { duration: 5000, panelClass: ['error-snackbar'], horizontalPosition: 'center', verticalPosition: 'top'});

    // Or using a simple alert for testing:
    // alert('This address already exists in the system.');
  }

  onEnter(event: any): void {
    const keyboardEvent = event as KeyboardEvent;
    
    if (!keyboardEvent || !(keyboardEvent instanceof KeyboardEvent)) {
      return;
    }

    keyboardEvent.preventDefault();
    keyboardEvent.stopImmediatePropagation();

    const currentStep = this.stepper.selectedIndex;
    console.log(currentStep);
    if (currentStep === 2) {
      console.log('Step 2 (Details)');

    }
    if (currentStep === 5) {           // Images step is now index 5
      console.log('Enter pressed on Images step → submitting');
      this.submitAndAdvance();
    } else if (!this.isCurrentStepInvalid()) {
      this.stepper.next();
    }
  }

  // Helper (optional but strongly recommended)
  private isCurrentStepInvalid(): boolean {
    const currentStep = this.stepper.selectedIndex;
    
    if (currentStep === 0) return this.addressGroup.invalid;
    if (currentStep === 1) return this.infoGroup.invalid;
    // if (currentStep === 2) return this.pricingRules.length === 0; // or whatever
    // ...
    
    return false;
  }

  setDefaultPriceRule() {
    console.log('Syncing default price rule...');
    const baseRateName = 'Default Base Rate';
    const costValue = this.infoGroup.get('cost')?.value;
    
    if (!costValue) return; //

    const priceInCents = parseInt(costValue) * 100;
    const index = this.pricingRules.findIndex(p => p.name === baseRateName); //

    if (index !== -1) {
       // Update existing rule
       this.pricingRules[index].priceCents = priceInCents; //
    } else {
       // Create it if it doesn't exist yet
       this.pricingRules.push({
          propertyId: '',
          name: baseRateName,
          priority: 10,
          priceCents: priceInCents,
          isActive: true,
          updatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString()
       });
       console.log('pricingRules', this.pricingRules);
    }
  }

  timezones$ = defer(() => this.propertyService.getTimezones());

  get propertyCalendars(): FormArray {
    return (this.propForm.get('propertyCalendars') as FormArray) ?? this.fb.array([]);
  }

  addCalendarItem(): void {
    const newItem = this.fb.group({
      start: ['', Validators.required],
      end: ['', Validators.required],
      name: [''],
      costPerDay: [null],
      costPerWeek: [null],
      costPerMonth: [null],
    });

    this.propertyCalendars.push(newItem);
    this.cdr.detectChanges();
  }

  removeCalendarItem(index: number): void {
    this.propertyCalendars.removeAt(index);
  }

  loadPricingRules() {
    
  }

  addPricingRule() {
    console.log('add pricing rule');
    const ref = this.dialogService.open(CreatePropertyAddPropertyPricingForm, 
      {
        width: '800px'
      }
    );

    ref.afterClosed().subscribe(result => {
      console.log('after closed pricing rule', result);
      if (result) {
        this.pricingRules.push(result);
        this.cdr.detectChanges();
      }
    });
  }

  async initAmenities(): Promise<void> {
    await this.loadAmenities();
    this.amenities = this.amenities.map(amenity => ({
      ...amenity, description: amenity.description ?? ''
    }));
    
    this.amenitiesFormArray = this.fb.array(
      this.amenities.map(() => this.fb.control(false))
    );
    this.propForm.setControl('amenities', this.amenitiesFormArray);
    this.cdr.detectChanges();
  }

  async loadAmenities(): Promise<void> {
    this.amenities = await firstValueFrom(this.propertyService.getAmenities());
  }

  getAmenitiesForCategory(category: string) {
    return this.amenities
      .map((amenity, globalIndex) => ({
        ...amenity,
        globalIndex
      }))
      .filter(amenity => amenity.category === category);
  }

  getSelectedAmenityIds(): string[] {
    const formArray = this.amenitiesGroup;
    const formArrayValues: boolean[] = formArray.value || []; 
    return formArrayValues
      .map((checked: boolean, i: number) => checked ? this.amenities[i].id : null)
      .filter(Boolean) as string[];
  }

  getSelectedAmenityNames(): string[] {
    return this.amenitiesFormArray.value
      .map((checked: boolean, i: number) => checked ? this.amenities[i].name : null)
      .filter(Boolean);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    
    if (files) {
      for (let i = 0; i < files.length; i++) {
        if (this.images.length < this.maxImages) {
          const file = files[i];
          if (this.isValidImage(file)) {
            this.images.push(file);
            this.createImagePreview(file);
          }
        }
      }
      this.cdr.detectChanges();
      this.propForm.patchValue({ images: this.images });
      this.syncImagesFormArray();
    }
  }

  onImagesSelected(event: any): void {
    const files: FileList = event.target.files;
    for (let i = 0; i < files.length; i++) {
      if (this.images.length < this.maxImages) {
        const file = files[i];
        if (this.isValidImage(file)) {
          this.images.push(file);
          this.createImagePreview(file);
          this.cdr.detectChanges();
        }
      }
    }
    this.propForm.patchValue({ images: this.images });
    this.syncImagesFormArray();
    this.cdr.detectChanges();
  }

  createImagePreview(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.imagePreviews.push(e.target.result);
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  onDragOver(event: DragEvent): void { event.preventDefault(); }
  
  isValidImage(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 10 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      alert('Only JPG, PNG, WebP allowed!');
      return false;
    }
    if (file.size > maxSize) {
      alert('Max 10MB per image!');
      return false;
    }
    return true;
  }

  removeImage(event: MouseEvent, index: number): void {
    event.preventDefault();
    this.images.splice(index, 1);
    this.imagePreviews.splice(index, 1);
    this.propForm.patchValue({ images: this.images });
    this.syncImagesFormArray();
    this.cdr.detectChanges();
  }

  async convertImagesToBase64(): Promise<CreatePropertyImageRequest[]> {
    this.isProcessingImages = true;
    this.processedImages = 0;
    this.totalImages = this.images.length;
    this.cdr.detectChanges();

    const createImgReq: CreatePropertyImageRequest[] = [];
    
    // This array (this.images) is already reordered by onDropImage
    for (let i = 0; i < this.images.length; i++) {
      const file = this.images[i];
      try {
        const base64 = await this.fileToBase64(file);
        const contentType = base64.split(',')[0].split(':')[1].split(';')[0];
        
        createImgReq.push({
          file: base64,
          contentType,
          sortOrder: i // The index 'i' now represents the user's chosen order
        });
        
        this.processedImages = i + 1;
        this.cdr.detectChanges();
      } catch (error) {
        console.error('Error processing image:', error);
      }
    }
    
    this.isProcessingImages = false;
    return createImgReq;
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  }
  
  onDropImage(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.imagePreviews, event.previousIndex, event.currentIndex);
    this.cdr.detectChanges();
    moveItemInArray(this.images, event.previousIndex, event.currentIndex);

    this.syncImagesFormArray();
    this.cdr.detectChanges();
    console.log('🔄 Reordered to:', this.imagePreviews.length, this.images, 'images');
  }

  syncImagesFormArray(): void {
    const imagesFormArray = this.imagesArray;
  
    // Clear and rebuild to ensure the length property is exactly what the validator sees
    imagesFormArray.clear(); 

    this.images.forEach(() => {
      imagesFormArray.push(this.fb.control(null)); 
    });

    // Force validation to run
    setTimeout(() => {
      imagesFormArray.updateValueAndValidity();
      this.cdr.detectChanges();
    });
  }

  get descriptionControl() { return this.propForm.get('info.description'); }

  async getLatLong(): Promise<[number, number]> {
    console.log('user', this.userId);
    const street = this.addressGroup.get('street')?.value || '';
    const city = this.addressGroup.get('city')?.value || '';
    const state = this.addressGroup.get('state')?.value || '';
    const zipCode = this.addressGroup.get('zipCode')?.value || '';
    
    const address = `${street}, ${city}, ${state} ${zipCode}`.trim();
    if (!address) throw new Error('Missing address');
    console.log('address to get', address);
    
    return new Promise((resolve, reject) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const lat = results[0].geometry.location.lat();
          const lng = results[0].geometry.location.lng();
          resolve([lat, lng]);
        } else {
          reject(new Error(`Invalid address: ${status}`));
        }
      });
    });
  }

  goToCreatedProperty = () => {
    if (this.propertyCreatedId !== undefined) {
      this.router.navigate(['/property', this.propertyCreatedId]);
    } else {
      console.log('property id not found');
    }
  }

  publishProperty = () => {
    console.log('publish', this.propertyCreatedId);
    this.router.navigate(['/property/publish/' + this.propertyCreatedId]);
  }

  submitAndAdvance() {
    this.submitted = true;   // ← Important: show validation errors immediately

    if (this.isProcessing || this.isProcessingImages || this.isSubmitting) {
      console.log('⛔ Submit already in progress - ignoring duplicate call');
      return;
    }

    this.isProcessing = true;
    this.isSubmitting = true;

    this.cdr.detectChanges();

    if (this.propForm.invalid) {
      this.propForm.markAllAsTouched();
      this.isProcessing = false;
      this.isSubmitting = false;
      this.cdr.detectChanges();
      return;
    }

    this.submit();
  }

  async submit() {
    this.isSubmitting = true;
    this.submitted = true;
    this.isProcessing = true;
    this.submissionError = null;
    this.partialPropertyId = null;

    this.initializeSubmissionSteps();

    try {
      console.log('🚀 Creating property...', this.propForm);

      // GEOCODE
      this.updateStep('geocode', 'active');
      const [lat, lng] = await this.getLatLong();
      this.updateStep('geocode', 'completed');

      // ADDRESS
      this.updateStep('create-address', 'active');
      const address: AddressRequest = {
        lat, lng,
        street: this.addressGroup.get('street')?.value || '',
        city: this.addressGroup.get('city')?.value || '',
        state: (this.addressGroup.get('state')?.value as State)?.code || '',
        zipCode: this.addressGroup.get('zipCode')?.value || '',
        country: 'USA'
      };
      const addressResult = await this.propertyService.createAddress(address).toPromise();
      if (!addressResult?.id) throw new Error('Failed to create address');
      this.updateStep('create-address', 'completed');

      // PROPERTY
      this.updateStep('create-property', 'active');
      const property: CreatePropertyRequest = {
        addressId: addressResult.id,
        displayName: this.addressGroup.get('displayName')?.value,
        propertyType: (this.infoGroup.get('propertyType')?.value as string),
        sizeSqft: this.infoGroup.get('sqFt')?.value,
        bedrooms: this.infoGroup.get('bedrooms')?.value,
        bathrooms: this.infoGroup.get('bathrooms')?.value,
        sleeps: this.infoGroup.get('sleeps')?.value,
        cost: this.infoGroup.get('cost')?.value,
        description: this.infoGroup.get('description')?.value,
        timezone: this.addressGroup.get('timezone')?.value,
        isPublished: false
      };
      const propertyResult = await this.propertyService.createProperty(property).toPromise();
      if (!propertyResult?.id) throw new Error('Failed to create property');

      this.propertyCreatedId = propertyResult.id;
      if (this.userId) {
        localStorage.setItem(this.getDraftKey(), this.propertyCreatedId);
      }
      this.updateStep('create-property', 'completed');

      // USER PROPERTY
      this.updateStep('assign-user', 'active');
      if (this.userId) {
        await this.propertyService.setUserProperty(propertyResult.id, this.userId).toPromise();
      }
      this.updateStep('assign-user', 'completed');

      // PRICING RULES (now non-fatal)
      this.updateStep('pricing-rules', 'active', `0/${this.pricingRules.length}`);
      let pricingDone = 0;
      for (const rule of this.pricingRules) {
        try {
          rule.propertyId = propertyResult.id;
          await this.pricingService.createPricingRule(rule).toPromise();
          pricingDone++;
          this.updateStep('pricing-rules', 'active', `${pricingDone}/${this.pricingRules.length}`);
        } catch (e) {
          console.warn('⚠️ Pricing rule failed (continuing anyway)', e);
        }
      }
      this.updateStep('pricing-rules', 'completed', `${pricingDone}/${this.pricingRules.length} saved`);

      // IMAGES
      this.updateStep('images', 'active');
      const imgRequests: CreatePropertyImageRequest[] = await this.convertImagesToBase64();
      console.log('imgRequest', imgRequests);
      for (const request of imgRequests) {
        try {
          await this.propertyService.createPropertyImage(propertyResult.id, request).toPromise();
        } catch (e) {
          console.warn('Image upload failed for one file', e);
        }
      }
      this.updateStep('images', 'completed', `${imgRequests.length} photos processed`);

      // CALENDARS
      const pcs = this.propForm.get('propertyCalendars')?.value;
      if (pcs && pcs.length > 0) {
        this.updateStep('calendars', 'active');
        const calendarRequests = pcs.map((item: any) => ({
          startDate: item.start, 
          endDate: item.end,
          name: item.name,
          costPerNight: item.costPerDay ?? null,
          costPerWeek: item.costPerWeek ?? null,
          costPerMonth: item.costPerMonth ?? null,
        }));
        await this.propertyService.setPropertyCalendars(propertyResult.id, calendarRequests).toPromise();
        this.updateStep('calendars', 'completed');
      } else {
        this.updateStep('calendars', 'completed');
      }

      // AMENITIES
      this.updateStep('amenities', 'active');
      const selectedIds = this.getSelectedAmenityIds();
      const request: PropertyAmenityBulkRequest = {
        propertyId: propertyResult.id,
        amenityIds: selectedIds
      };
      await this.propertyService.setPropertyAmenities(request).toPromise();
      this.updateStep('amenities', 'completed');

      // SUCCESS
      console.log('🎉 PROPERTY CREATED! Redirecting to payment...');
      this.trackPropertyCreation(propertyResult.id);
      this.clearDraft();
      this.successfulSubmission = true;

      this.snackbar.open('Property created successfully! Redirecting to payment...', 'OK', { 
        duration: 1500, 
        panelClass: ['success-snackbar'] 
      });

      setTimeout(() => {
        this.router.navigate([`/payment/${propertyResult.id}`]);
      }, 800);

    } catch (error: any) {
      console.error('💥 Error:', error);
      this.submissionError = this.getFriendlyErrorMessage(error);

      if (this.propertyCreatedId) {
        this.partialPropertyId = this.propertyCreatedId;
      } else {
        this.clearDraft();
      }

      this.snackbar.open(this.submissionError, 'OK', { 
        duration: 6000, 
        panelClass: ['error-snackbar'] 
      });
    } finally {
      this.isProcessing = false;
      this.isSubmitting = false;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }
  }

  private getFriendlyErrorMessage(err: any): string {
    if (err?.message?.includes('address')) return 'We had trouble validating your address. Please double-check it and try again.';
    if (err?.message?.includes('image') || err?.message?.includes('upload')) return 'Some photos failed to upload. You can still proceed and add more later.';
    return 'Something unexpected happened while creating your listing. Don\'t worry — we saved what we could.';
  }

  public confetti() {
    if (isPlatformBrowser(this.platformId)) {
      const duration = 15 * 300;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

      function randomInRange(min: number, max: number) {
        return Math.random() * (max - min) + min;
      }

      var interval = setInterval(function() {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        // since particles fall down, start a bit higher than random
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
      }, 250);
    }
  }

  trackPropertyCreation(propertyId: string) {
    this.gtm.pushEvent('created_property', {
      property_id: propertyId,
      user_id: this.userId
    });
  }

  onContinueToPayment() {
    // Your logic: navigate to payment, open dialog, etc.
    console.log('User clicked Continue — start activation flow', this.propertyCreatedId);
    this.router.navigate([`/payment/${this.propertyCreatedId}`]);
  }

  proceedToPaymentAnyway(): void {
    const id = this.partialPropertyId || this.propertyCreatedId;
    if (id) {
      this.router.navigate([`/payment/${id}`]);
    }
  }

  retrySubmission(): void {
    this.submissionError = null;
    this.partialPropertyId = null;
    this.submit(); // or just re-enable the original button
  }

  goToMyProperties(): void {
    this.router.navigate(['/properties']); // or wherever your dashboard is
  }

  contactSupport() {

  }
}